import React, { useState, useEffect, useRef } from 'react';
import { getCookie } from '../utils';

export default function EnrolledPlanCard({ 
  enroll, 
  batches, 
  setEnrollments, 
  globalActiveEnrollmentId, 
  setGlobalActiveEnrollmentId 
}) {
  // --- LOCAL SESSION & TIMER LOGIC STATE ---
  const [activeSession, setActiveSession] = useState(null); 
  const [elapsed, setElapsed] = useState(0); 

  const sessionRef = useRef(activeSession);
  useEffect(() => {
    sessionRef.current = activeSession;
  }, [activeSession]);

  // --- CHECK ON LOAD: Resume existing session if one is active ---
  useEffect(() => {
    let isMounted = true;
    
    const checkActiveSessionOnLoad = async () => {
      try {
        const token = getCookie('tsa_token');
        const res = await fetch('https://tsaapis.operate.live/api/attendance', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'x-auth-token': token || '' },
          credentials: 'include'
        });

        if (res.ok) {
          const history = await res.json();
          // Find if there is an active session (missing LogoutDT) specific to this enrollment
          const myActiveRecord = history.find(a => 
            (a.EnrolmentId === enroll.id || (a.EnrolmentId && a.EnrolmentId._id === enroll.id)) 
            && !a.LogoutDT
          );

          if (myActiveRecord && isMounted) {
            const loginTime = new Date(myActiveRecord.LoginDT).getTime();
            const nowTime = Date.now();
            const currentElapsed = Math.floor((nowTime - loginTime) / 1000);
            const currentBilled = Math.floor(currentElapsed / 3600) + 1;

            setActiveSession({
              enrollId: enroll.id,
              attendanceId: myActiveRecord._id || myActiveRecord.id,
              startTime: loginTime,
              billedHours: currentBilled
            });
            setElapsed(currentElapsed);
            
            // Lock out the other cards by informing the parent
            setGlobalActiveEnrollmentId(enroll.id);
          }
        }
      } catch (err) {
        console.error("Error checking load session:", err);
      }
    };

    checkActiveSessionOnLoad();

    return () => { isMounted = false; };
  }, [enroll.id, setGlobalActiveEnrollmentId]);

  // --- TIMERS & RUNTIME BACKGROUND AUTO-BILLING THREAD ---
  useEffect(() => {
    let interval;
    if (activeSession) {
      interval = setInterval(() => {
        const currentSession = sessionRef.current;
        if (!currentSession) return;

        const currentElapsed = Math.floor((Date.now() - currentSession.startTime) / 1000);
        setElapsed(currentElapsed);
        
        const expectedBilledHours = Math.floor(currentElapsed / 3600) + 1;
        
        // Automated block deduction step upon boundary cross
        if (expectedBilledHours > currentSession.billedHours) {
          autoBillAnotherHour(currentSession.enrollId, expectedBilledHours);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession]);


  // --- REST ENDPOINT INTERFACES ---

  const autoBillAnotherHour = async (enrollId, newBilledCount) => {
    setActiveSession(prev => prev ? { ...prev, billedHours: newBilledCount } : null);
    
    const newUsedHours = parseFloat(enroll.usedHours) + 1;

    try {
      const token = getCookie('tsa_token');
      await fetch(`https://tsaapis.operate.live/api/enrolments/${enrollId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token || '' },
        body: JSON.stringify({ UsedHours: newUsedHours })
      });

      setEnrollments(prev => prev.map(e => 
        e.id === enrollId ? { ...e, usedHours: newUsedHours, balanceHours: Math.max(0, e.balanceHours - 1) } : e
      ));
    } catch (err) {
      console.error("Auto-bill API Error:", err);
    }
  };

  const handleLoginSession = async () => {
    // 1. Enrollment State Checks
    if (enroll.active === false || enroll.isActive === false) {
      return alert("This enrollment plan is currently inactive. You cannot check in.");
    }
    if (globalActiveEnrollmentId && globalActiveEnrollmentId !== enroll.id) {
      return alert("Another training plan is currently active! Please check-out of it first.");
    }
    if (activeSession) return alert("You already have an active session running!");
    if (enroll.balanceHours <= 0) return alert("You have exhausted your hours for this plan. Please renew.");

    try {
      const token = getCookie('tsa_token');
      const apiHeaders = { 'Content-Type': 'application/json', 'x-auth-token': token || '' };
      const now = new Date();
      const nowTime = now.getTime();

      let billingStartTime = nowTime;
      let currentBilled = 1;
      let shouldDeductNow = true;

      const checkRes = await fetch('https://tsaapis.operate.live/api/attendance', {
        method: 'GET',
        headers: apiHeaders,
        credentials: 'include'
      });

      if (checkRes.ok) {
        const history = await checkRes.json();
        const myHistory = history.filter(a => 
          a.EnrolmentId === enroll.id || (a.EnrolmentId && a.EnrolmentId._id === enroll.id)
        );

        if (myHistory.length > 0) {
          myHistory.sort((a, b) => new Date(a.LoginDT).getTime() - new Date(b.LoginDT).getTime());
          let ticketExpiry = 0;

          myHistory.forEach(record => {
            if (!record.LoginDT) return;
            const loginTime = new Date(record.LoginDT).getTime();
            const logoutTime = record.LogoutDT ? new Date(record.LogoutDT).getTime() : nowTime;
            
            if (loginTime >= ticketExpiry) {
              let duration = (logoutTime - loginTime) / 3600000;
              if (duration <= 0) duration = 0.0001; 
              ticketExpiry = loginTime + (Math.ceil(duration) * 3600000);
            } else {
              if (logoutTime > ticketExpiry) {
                let extra = (logoutTime - ticketExpiry) / 3600000;
                if (extra <= 0) extra = 0.0001;
                ticketExpiry += (Math.ceil(extra) * 3600000);
              }
            }
          });

          if (nowTime < ticketExpiry) {
            shouldDeductNow = false;
            billingStartTime = ticketExpiry - 3600000; 
          }
        }
      }

      const attRes = await fetch('https://tsaapis.operate.live/api/attendance', {
        method: 'POST',
        credentials: 'include',
        headers: apiHeaders,
        body: JSON.stringify({ EnrolmentId: enroll.id, LoginDT: now.toISOString() })
      });
      if (!attRes.ok) throw new Error("Failed to clock in");
      const attData = await attRes.json();
      const attendanceId = attData._id || attData.id;

      if (shouldDeductNow) {
        const newUsedHours = parseFloat(enroll.usedHours) + 1;
        await fetch(`https://tsaapis.operate.live/api/enrolments/${enroll.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: apiHeaders,
          body: JSON.stringify({ UsedHours: newUsedHours })
        });

        setEnrollments(prev => prev.map(e => 
          e.id === enroll.id ? { ...e, usedHours: newUsedHours, balanceHours: Math.max(0, e.balanceHours - 1) } : e
        ));
      }

      setActiveSession({ 
        enrollId: enroll.id, 
        attendanceId: attendanceId, 
        startTime: billingStartTime,
        billedHours: currentBilled 
      });
      setElapsed(Math.floor((nowTime - billingStartTime) / 1000));

      // 2. Lock other plans by updating parent state
      setGlobalActiveEnrollmentId(enroll.id);

    } catch (err) {
      console.error("Login Error:", err);
      alert("Something went wrong starting your session. Please check your connection.");
    }
  };

  const handleLogoutSession = async () => {
    if (!activeSession) return;

    try {
      const token = getCookie('tsa_token');
      await fetch(`https://tsaapis.operate.live/api/attendance/${activeSession.attendanceId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token || '' },
        body: JSON.stringify({ LogoutDT: new Date().toISOString() })
      });
      
      setActiveSession(null);
      setElapsed(0);

      // Free up other plans
      setGlobalActiveEnrollmentId(null);
    } catch (err) {
      console.error("Logout Error:", err);
      alert("Error logging out of session.");
    }
  };

  const formatTimer = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const batch = batches.find(b => b.id === enroll.batchId);
  if (!batch) return null;

  const used = parseFloat(enroll.usedHours) || 0;
  const balance = parseFloat(enroll.balanceHours) || 0;
  const totalHours = used + balance;
  const progressPercent = totalHours > 0 ? (used / totalHours) * 100 : 0;
  
  const isEnrollmentActive = enroll.active !== false && enroll.isActive !== false;
  const isLowBalance = balance <= 8 && balance > 0;
  const isExhausted = balance <= 0;
  const isCurrentlyActive = activeSession !== null;
  const isLockedByAnotherPlan = globalActiveEnrollmentId !== null && globalActiveEnrollmentId !== enroll.id;

  // Build card borders dynamically based on state flags
  const getCardBorder = () => {
    if (!isEnrollmentActive) return '1px solid #4b5563'; // Gray for inactive
    if (isExhausted || isLowBalance) return '1px solid #ef4444';
    if (isCurrentlyActive) return '1px solid #3b82f6';
    return '1px solid rgba(255,255,255,0.05)';
  };

  return (
    <div style={{ 
      backgroundColor: 'rgba(0,0,0,0.2)', 
      padding: '20px', 
      borderRadius: '10px', 
      border: getCardBorder(),
      position: 'relative', 
      overflow: 'hidden', 
      transition: 'border 0.3s ease',
      opacity: (isLockedByAnotherPlan || !isEnrollmentActive) ? 0.6 : 1
    }}>
      {/* Alert Ribbon for Low Balance state */}
      {isLowBalance && !isExhausted && isEnrollmentActive && (
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          backgroundColor: 'rgba(239, 68, 68, 0.2)', 
          padding: '5px 20px', 
          color: '#fca5a5', 
          fontSize: '0.85rem', 
          fontWeight: 'bold', 
          display: 'flex', 
          justifyContent: 'space-between' 
        }}>
          <span><i className="fas fa-exclamation-triangle"></i> Low Balance Alert</span>
          <span>Please renew soon to avoid class interruption.</span>
        </div>
      )}
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: (isLowBalance && isEnrollmentActive) ? '25px' : '0', flexWrap: 'wrap', gap: '15px' }}>
        
        {/* LEFT PROFILE INFO */}
        <div>
          <h4 style={{ color: 'var(--accent-gold)', fontSize: '1.3rem', margin: '0 0 5px 0' }}>
            {batch.title}
            {!isEnrollmentActive && (
              <span className="badge" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: '1px solid #ef4444', marginLeft: '10px', fontSize: '0.7rem', verticalAlign: 'middle' }}>
                Inactive
              </span>
            )}
          </h4>
          <span className="badge">{batch.type}</span>
          <p style={{ color: 'var(--text-gray)', margin: '10px 0 0 0', fontSize: '0.9rem' }}>
            <i className="far fa-calendar-alt"></i> Valid: {enroll.startDay} to {enroll.endDay}
          </p>
        </div>
        
        {/* RIGHT METRICS & CONTROLS */}
        <div style={{ textAlign: 'right', minWidth: '240px', flex: 1, maxWidth: '320px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: 'var(--text-gray)', fontSize: '0.9rem' }}>Used: <strong style={{color:'white'}}>{used.toFixed(2)}</strong></div>
              <div style={{ color: isExhausted || isLowBalance ? '#ef4444' : '#10b981', fontWeight: 'bold', fontSize: '0.9rem' }}>{balance.toFixed(2)} Hrs Left</div>
            </div>

            {/* Render Context-Aware Session Management Triggers */}
            {isCurrentlyActive ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                <span style={{ fontFamily: 'monospace', fontSize: '1rem', color: '#38bdf8', fontWeight: 'bold', background: 'rgba(56, 189, 248, 0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                  <i className="fas fa-circle" style={{ color: '#ef4444', fontSize: '0.6rem', marginRight: '5px', animation: 'blink 1s infinite' }}></i>
                  {formatTimer(elapsed)}
                </span>
                <button 
                  className="btn-small" 
                  style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '4px 15px', fontSize: '0.85rem' }} 
                  onClick={handleLogoutSession}
                >
                  Check-out
                </button>
              </div>
            ) : isExhausted ? (
              <button 
                className="btn-small" 
                style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '8px 20px' }} 
                onClick={() => alert(`Please contact reception to renew ${batch.title}...`)}
              >
                <i className="fas fa-sync-alt"></i> Renew
              </button>
            ) : (
              <button 
                className="btn-small" 
                style={{ 
                  backgroundColor: (!isEnrollmentActive || isLockedByAnotherPlan) ? '#4b5563' : '#3b82f6', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 20px', 
                  cursor: (!isEnrollmentActive || isLockedByAnotherPlan) ? 'not-allowed' : 'pointer',
                  opacity: (!isEnrollmentActive || isLockedByAnotherPlan) ? 0.7 : 1 
                }} 
                onClick={handleLoginSession}
                disabled={!isEnrollmentActive || isLockedByAnotherPlan}
              >
                <i className={!isEnrollmentActive ? "fas fa-ban" : "fas fa-sign-in-alt"}></i> 
                {!isEnrollmentActive ? ' Inactive' : isLockedByAnotherPlan ? ' Locked' : ' Check-in'}
              </button>
            )}

          </div>
          
          {/* BAR LEVEL */}
          <div className="progress-bar-bg" style={{ marginBottom: '15px' }}>
            <div 
              className="progress-bar-fill" 
              style={{ 
                width: `${progressPercent}%`, 
                backgroundColor: isExhausted || isLowBalance ? '#ef4444' : 'var(--accent-gold)',
                filter: !isEnrollmentActive ? 'grayscale(100%)' : 'none'
              }}
            ></div>
          </div>
          
          {/* Quick Action Plan Renewal Link */}
          {(isLowBalance && !isExhausted && isEnrollmentActive) && (
            <button 
              className="submit-btn" 
              style={{ padding: '8px 15px', fontSize: '0.9rem', width: '100%', backgroundColor: '#ef4444', border: 'none' }}
              onClick={() => alert(`Please contact reception to renew ${batch.title}...`)}
            >
              <i className="fas fa-sync-alt"></i> Renew Plan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}